import * as THREE from 'three'
import Terrain from '../terrain'
import { ItemId } from '../items'

type MobKind = 'animal' | 'monster'
type AnimalSpecies = 'pig' | 'cow' | 'sheep'
type MonsterSpecies = 'zombie' | 'creeper' | 'skeleton'

type Mob = {
  id: number
  kind: MobKind
  species: AnimalSpecies | MonsterSpecies
  group: THREE.Group
  health: number
  maxHealth: number
  bar: HTMLDivElement
}

const ANIMAL_SPECIES: AnimalSpecies[] = ['pig', 'cow', 'sheep']
const MONSTER_SPECIES: MonsterSpecies[] = ['zombie', 'creeper', 'skeleton']

const SPECIES_NAMES: Record<AnimalSpecies | MonsterSpecies, string> = {
  pig: '猪',
  cow: '牛',
  sheep: '羊',
  zombie: '僵尸',
  creeper: '苦力怕',
  skeleton: '骷髅'
}

const mobGeometry = new THREE.BoxGeometry(1, 1, 1)

// animal materials
const pigBodyMaterial = new THREE.MeshStandardMaterial({ color: 0xf0a4ad })
const pigDarkMaterial = new THREE.MeshStandardMaterial({ color: 0xe08b95 })
const cowBodyMaterial = new THREE.MeshStandardMaterial({ color: 0x59422f })
const cowHeadMaterial = new THREE.MeshStandardMaterial({ color: 0x6e5138 })
const cowSnoutMaterial = new THREE.MeshStandardMaterial({ color: 0xd9c9b8 })
const sheepWoolMaterial = new THREE.MeshStandardMaterial({ color: 0xededed })
const sheepFaceMaterial = new THREE.MeshStandardMaterial({ color: 0xd6c6b4 })

// monster materials
const zombieSkinMaterial = new THREE.MeshStandardMaterial({ color: 0x4f9155 })
const zombieShirtMaterial = new THREE.MeshStandardMaterial({ color: 0x3d6b6e })
const zombiePantsMaterial = new THREE.MeshStandardMaterial({ color: 0x3b3f8c })
const creeperMaterial = new THREE.MeshStandardMaterial({ color: 0x4f9c3e })
const creeperDarkMaterial = new THREE.MeshStandardMaterial({ color: 0x3e7c30 })
const skeletonMaterial = new THREE.MeshStandardMaterial({ color: 0xd8d8d8 })
const skeletonDarkMaterial = new THREE.MeshStandardMaterial({ color: 0xb8b8b8 })
const eyeMaterial = new THREE.MeshStandardMaterial({ color: 0x1a1a1a })

export default class Mobs {
  constructor(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    terrain: Terrain
  ) {
    this.scene = scene
    this.camera = camera
    this.terrain = terrain
    this.spawnInitialMobs()
  }

  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  terrain: Terrain
  mobs: Mob[] = []
  raycaster = new THREE.Raycaster()
  vector = new THREE.Vector3()
  lastMoveTime = 0
  isActive = false

  setActive(active: boolean) {
    this.isActive = active
    for (const mob of this.mobs) {
      mob.bar.classList.toggle('hidden', !active)
    }
  }

  attackFromCamera(): { drop: ItemId | null; position: THREE.Vector3 } | null {
    this.raycaster.setFromCamera({ x: 0, y: 0 }, this.camera)
    this.raycaster.far = 6
    const intersections = this.raycaster.intersectObjects(
      this.mobs.map(mob => mob.group),
      true
    )
    const target = intersections[0]?.object.parent
    const mob = this.mobs.find(item => item.group === target)
    if (!mob) {
      return null
    }

    mob.health = Math.max(0, mob.health - 6)
    if (mob.health === 0) {
      const position = mob.group.position.clone()
      this.scene.remove(mob.group)
      mob.bar.remove()
      this.mobs = this.mobs.filter(item => item !== mob)
      return {
        drop: mob.kind === 'animal' ? 'beef' : 'rottenFlesh',
        position
      }
    }
    return { drop: null, position: mob.group.position }
  }

  update() {
    if (!this.isActive) {
      return
    }

    const now = performance.now()
    if (now - this.lastMoveTime > 600) {
      this.lastMoveTime = now
      for (const mob of this.mobs) {
        if (mob.kind === 'monster') {
          const direction = this.camera.position
            .clone()
            .sub(mob.group.position)
            .setY(0)
          if (direction.length() < 24 && direction.length() > 2) {
            direction.normalize()
            mob.group.position.add(direction.multiplyScalar(0.25))
          }
        } else {
          mob.group.rotation.y += 0.35
        }
      }
    }

    for (const mob of this.mobs) {
      this.updateHealthBar(mob)
    }
  }

  private spawnInitialMobs() {
    const positions: Array<[MobKind, number, number]> = [
      ['animal', -10, 12],
      ['animal', -15, 18],
      ['animal', 8, 18],
      ['animal', 15, 22],
      ['animal', 18, 8],
      ['monster', -14, -14],
      ['monster', 8, -18],
      ['monster', 18, -12],
      ['monster', -22, -6],
      ['monster', 22, 4]
    ]

    for (let i = 0; i < positions.length; i++) {
      const [kind, x, z] = positions[i]
      const species =
        kind === 'animal'
          ? ANIMAL_SPECIES[Math.floor(Math.random() * ANIMAL_SPECIES.length)]
          : MONSTER_SPECIES[Math.floor(Math.random() * MONSTER_SPECIES.length)]
      this.addMob(i, kind, species, x, z)
    }
  }

  private addPart(
    group: THREE.Group,
    material: THREE.Material,
    scale: [number, number, number],
    position: [number, number, number],
    rotationX = 0
  ) {
    const part = new THREE.Mesh(mobGeometry, material)
    part.scale.set(scale[0], scale[1], scale[2])
    part.position.set(position[0], position[1], position[2])
    part.rotation.x = rotationX
    group.add(part)
  }

  private addMob(
    id: number,
    kind: MobKind,
    species: AnimalSpecies | MonsterSpecies,
    x: number,
    z: number
  ) {
    const group = new THREE.Group()
    if (kind === 'animal') {
      this.buildAnimal(group, species as AnimalSpecies)
    } else {
      this.buildMonster(group, species as MonsterSpecies)
    }

    const heightOffset = Math.floor(
      this.terrain.noise.get(
        x / this.terrain.noise.gap,
        z / this.terrain.noise.gap,
        this.terrain.noise.seed
      ) * this.terrain.noise.amp
    )
    group.position.set(x, 31 + heightOffset, z)
    group.name = SPECIES_NAMES[species]
    this.scene.add(group)

    const bar = document.createElement('div')
    bar.className = `mob-health ${kind}`
    bar.classList.add('hidden')
    document.body.appendChild(bar)

    this.mobs.push({
      id,
      kind,
      species,
      group,
      health: kind === 'animal' ? 10 : 16,
      maxHealth: kind === 'animal' ? 10 : 16,
      bar
    })
  }

  private buildAnimal(group: THREE.Group, species: AnimalSpecies) {
    if (species === 'pig') {
      this.addPart(group, pigBodyMaterial, [1.45, 0.72, 0.72], [0, 0.9, 0])
      this.addPart(group, pigBodyMaterial, [0.6, 0.58, 0.58], [0, 1.05, -0.8])
      this.addPart(group, pigDarkMaterial, [0.26, 0.2, 0.08], [0, 0.98, -1.12])
      for (const x of [-0.45, 0.45]) {
        for (const z of [-0.32, 0.32]) {
          this.addPart(group, pigDarkMaterial, [0.24, 0.72, 0.24], [x, 0.28, z])
        }
      }
      return
    }

    if (species === 'cow') {
      this.addPart(group, cowBodyMaterial, [1.55, 0.8, 0.75], [0, 0.98, 0])
      this.addPart(group, cowHeadMaterial, [0.62, 0.6, 0.6], [0, 1.28, -0.82])
      this.addPart(group, cowSnoutMaterial, [0.32, 0.24, 0.1], [0, 1.14, -1.15])
      this.addPart(group, cowSnoutMaterial, [0.1, 0.16, 0.1], [-0.26, 1.62, -0.82])
      this.addPart(group, cowSnoutMaterial, [0.1, 0.16, 0.1], [0.26, 1.62, -0.82])
      for (const x of [-0.48, 0.48]) {
        for (const z of [-0.34, 0.34]) {
          this.addPart(group, cowBodyMaterial, [0.26, 0.78, 0.26], [x, 0.39, z])
        }
      }
      return
    }

    // sheep
    this.addPart(group, sheepWoolMaterial, [1.5, 0.85, 0.78], [0, 0.98, 0])
    this.addPart(group, sheepFaceMaterial, [0.56, 0.56, 0.56], [0, 1.2, -0.82])
    for (const x of [-0.45, 0.45]) {
      for (const z of [-0.32, 0.32]) {
        this.addPart(group, sheepFaceMaterial, [0.22, 0.6, 0.22], [x, 0.3, z])
      }
    }
  }

  private buildMonster(group: THREE.Group, species: MonsterSpecies) {
    if (species === 'zombie') {
      this.addPart(group, zombieSkinMaterial, [0.62, 0.62, 0.62], [0, 2.18, 0])
      this.addPart(group, eyeMaterial, [0.1, 0.1, 0.04], [-0.15, 2.24, -0.32])
      this.addPart(group, eyeMaterial, [0.1, 0.1, 0.04], [0.15, 2.24, -0.32])
      this.addPart(group, zombieShirtMaterial, [0.72, 1.05, 0.38], [0, 1.35, 0])
      // arms stretched forward
      this.addPart(
        group,
        zombieSkinMaterial,
        [0.24, 0.95, 0.24],
        [-0.58, 1.78, -0.42],
        -Math.PI / 2
      )
      this.addPart(
        group,
        zombieSkinMaterial,
        [0.24, 0.95, 0.24],
        [0.58, 1.78, -0.42],
        -Math.PI / 2
      )
      this.addPart(group, zombiePantsMaterial, [0.28, 0.9, 0.28], [-0.22, 0.38, 0])
      this.addPart(group, zombiePantsMaterial, [0.28, 0.9, 0.28], [0.22, 0.38, 0])
      return
    }

    if (species === 'creeper') {
      this.addPart(group, creeperMaterial, [0.7, 0.7, 0.7], [0, 2.0, -0.05])
      this.addPart(group, eyeMaterial, [0.12, 0.12, 0.04], [-0.16, 2.08, -0.41])
      this.addPart(group, eyeMaterial, [0.12, 0.12, 0.04], [0.16, 2.08, -0.41])
      this.addPart(group, eyeMaterial, [0.14, 0.24, 0.04], [0, 1.86, -0.41])
      this.addPart(group, creeperMaterial, [0.52, 1.3, 0.52], [0, 1.15, 0])
      for (const x of [-0.2, 0.2]) {
        for (const z of [-0.2, 0.2]) {
          this.addPart(group, creeperDarkMaterial, [0.28, 0.5, 0.28], [x, 0.25, z])
        }
      }
      return
    }

    // skeleton
    this.addPart(group, skeletonMaterial, [0.6, 0.6, 0.6], [0, 2.18, 0])
    this.addPart(group, eyeMaterial, [0.1, 0.1, 0.04], [-0.14, 2.22, -0.31])
    this.addPart(group, eyeMaterial, [0.1, 0.1, 0.04], [0.14, 2.22, -0.31])
    this.addPart(group, skeletonDarkMaterial, [0.58, 1.0, 0.3], [0, 1.35, 0])
    this.addPart(group, skeletonMaterial, [0.18, 0.9, 0.18], [-0.48, 1.3, 0])
    this.addPart(group, skeletonMaterial, [0.18, 0.9, 0.18], [0.48, 1.3, 0])
    this.addPart(group, skeletonMaterial, [0.22, 0.9, 0.22], [-0.2, 0.38, 0])
    this.addPart(group, skeletonMaterial, [0.22, 0.9, 0.22], [0.2, 0.38, 0])
  }

  private updateHealthBar(mob: Mob) {
    this.vector.copy(mob.group.position)
    this.vector.y += 2
    this.vector.project(this.camera)

    const hidden =
      this.vector.z < -1 ||
      this.vector.z > 1 ||
      mob.group.position.distanceTo(this.camera.position) > 48

    if (hidden) {
      mob.bar.classList.add('hidden')
      return
    }

    const x = (this.vector.x * 0.5 + 0.5) * window.innerWidth
    const y = (-this.vector.y * 0.5 + 0.5) * window.innerHeight
    const ratio = mob.health / mob.maxHealth

    mob.bar.classList.remove('hidden')
    mob.bar.style.left = `${x}px`
    mob.bar.style.top = `${y}px`
    mob.bar.innerHTML = `<span style="width:${ratio * 100}%"></span>`
  }
}
